/**
 * The Reader side of a stored analysis (#98 part 4,
 * docs/nextcloud-stored-analysis-design.md): the bar above the document
 * ("Loaded into IMSight on …", outdated / changed; Process re-runs the check)
 * and the document type from the file name.
 */

import type { StoredAnalysis } from '../core/integrations-service';
import type { TranslationParams } from '../i18n/ui-manager.types';
import { DOCUMENT_FORMATS } from '../core/app-constants';

const K = 'modules.reader.storedAnalysis';

/** The MIME type for a file name (the stored analysis has no file). */
export function storedDocumentType(name: string): string {
  const dot = name.lastIndexOf('.');
  const extension = dot < 0 ? '' : name.slice(dot).toLowerCase();
  const format = Object.values(DOCUMENT_FORMATS).find((f) =>
    f.extensions.includes(extension)
  );
  return format?.mimeTypes[0] ?? 'application/octet-stream';
}

export function renderStoredAnalysisBar(
  bar: HTMLElement,
  stored: StoredAnalysis,
  t: (key: string, params?: TranslationParams) => string
): void {
  const { analyzedAt, outdated, changed } = stored.analysis;
  const date = document.createElement('span');
  date.dataset.storedAnalysisDate = '';
  date.textContent = t(`${K}.from`, {
    date: analyzedAt ? new Date(analyzedAt).toLocaleString() : '—',
  });
  const parts: HTMLElement[] = [date];
  for (const [on, key] of [
    [outdated, 'outdated'],
    [changed, 'changed'],
  ] as const) {
    if (!on) continue;
    const flag = document.createElement('span');
    flag.className = 'stored-analysis-bar__flag';
    flag.dataset.storedAnalysisFlag = key;
    flag.textContent = t(`${K}.${key}`);
    parts.push(flag);
  }
  bar.replaceChildren(...parts);
  bar.hidden = false;
}

export function hideStoredAnalysisBar(bar: HTMLElement | null): void {
  if (!bar) return;
  bar.replaceChildren();
  bar.hidden = true;
}
